/**
 * HELP-04: Fetch capture with Sentry coexistence and idempotent wrapping.
 *
 * Key requirements (D-12):
 * 1. Wrap the CURRENT window.fetch (which may already be Sentry's wrapper).
 *    Never assign the native fetch directly — always chain the current one.
 * 2. If the current fetch carries __sentry_original__, we call through it
 *    without overwriting or stripping that property.
 * 3. The wrapped function carries __support_capture__ = true as an idempotent
 *    sentinel — calling installFetchCapture twice is a no-op.
 * 4. Captured entries are { ts, method, url (redacted via redactUrl), status, duration }.
 */
import { redactUrl } from './redact';
import type { NetworkLogEntry } from '@support-system/shared';

type RecordFn = (entry: NetworkLogEntry) => void;

/**
 * Install a fetch capture wrapper.
 *
 * @param record - Callback that receives each captured NetworkLogEntry.
 * @returns A dispose function that restores the previous window.fetch and removes the wrapper.
 */
export function installFetchCapture(record: RecordFn): () => void {
    // Idempotent sentinel — do NOT double-wrap
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window.fetch as any).__support_capture__) {
        return () => { /* already installed — no-op dispose */ };
    }

    // Capture the CURRENT fetch (may be a Sentry wrapper with __sentry_original__)
    const previous = window.fetch;

    const wrapped = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const method = (init?.method ?? (typeof input === 'string' || input instanceof URL ? 'GET' : (input as Request).method)) || 'GET';
        const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
        const ts = Date.now();

        const response = await previous.call(window, input, init);

        const duration = Date.now() - ts;
        const entry: NetworkLogEntry = {
            ts,
            method: method.toUpperCase(),
            url: redactUrl(rawUrl),
            status: response.status,
            duration,
        };
        record(entry);

        return response;
    } as typeof window.fetch;

    // Mark idempotent sentinel
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (wrapped as any).__support_capture__ = true;

    window.fetch = wrapped;

    return () => {
        window.fetch = previous;
    };
}
